import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { FormInputCheckbox } from "..";

/**
 * @description Radio button combo input - version 1, with label on top, yes/no radio buttons below
 * 
 * @component
 * 
 * @param {Object} props
 * @param {string} props.name - The name attribute for the radio button group
 * @param {import("react").ReactNode | string} props.label - The label for the radio button group
 * @param {import("react").ReactNode | string} [props.errorMessage] - The error message to display when validation fails
 * @param {boolean} [props.required=false] - Whether the input is required
 * @param {import("react").ReactNode} [props.children] - Additional children elements to render within the component
 * @param {React.MouseEventHandler<HTMLInputElement> | undefined} [props.onClick] - Click event handler for the radio buttons
 * @param {Function| undefined} [props.onChange] - Change event handler for the radio buttons
 * @param {Function | undefined} [props.onValidate] - Custom validation function
 * @param {Object} ref - The ref object to be forwarded to the parent component
 * @returns {import("react").JSX.Element} The rendered component
 */
function FormInputRadioCombo1({
  name = null,
  label = null,
  errorMessage = <>Please make a selection</>,
  required = false,
  children = null,
  onClick = undefined,
  onChange = undefined,
  onValidate = undefined,
  ...rest
}, ref) {
  const tempRef = useRef(null);
  const componentRef = ref || tempRef;
  const buttonYesRef = useRef(null);
  const buttonNoRef = useRef(null);
  const [valid, setValid] = useState(true);

  const testValidation = () => {
    const yesChecked = buttonYesRef.current?.checked;
    const noChecked = buttonNoRef.current?.checked;
    const isValid = yesChecked || noChecked;

    return isValid && required || !required;
  };

  function validate() {
    const valid = testValidation();
    setValid(valid);

    onValidate && onValidate(valid);
    return valid;
  }

  useImperativeHandle(componentRef, () => ({
    name,
    validate,
    testValidation,
    setValid,
    reset() {
      setValid(false);
      if (buttonYesRef.current) buttonYesRef.current.checked = false;
      if (buttonNoRef.current) buttonNoRef.current.checked = false;
    },
    get value() {
      if (buttonYesRef.current?.checked) return true;
      else if (buttonNoRef.current?.checked) return false;
      else return null;
    },
    setValue(val) {
      if (val === true && buttonYesRef.current) {
        buttonYesRef.current?.setCheck(true);
        buttonNoRef.current?.setCheck(false);
      } else if (val === false && buttonNoRef.current) {
        buttonNoRef.current?.setCheck(true);
        buttonYesRef.current?.setCheck(false);
      } else {
        if (buttonYesRef.current) buttonYesRef.current?.setCheck(false);
        if (buttonNoRef.current) buttonNoRef.current?.setCheck(false);
      }
    }, get type() {
      return 'radio-combo-1';
    },
    setFocus() {
      setTimeout(() => {
        buttonYesRef.current?.setFocus();
      }, 100);
    },
  }));

  function onClickHandler(e) {
    onClick && onClick(e);
  }

  function onChangeHandler(e) {
    validate();
    onChange && onChange(e);
  }

  return (
    <fieldset
      className={`form-input-radio-combo-1 ${valid ? '' : 'form-input-radio-combo-1--invalid'}`.trim()}
      {...rest}
    >
      {label && <legend>{label}</legend>}
      <div className="form-input-radio-combo-1__options" onBlur={() => setTimeout(validate, 0)}>
        <FormInputCheckbox
          ref={buttonYesRef}
          name={`${name}-yes`}
          typeRadio
          radioGroupName={name}
          aria-errormessage={!valid && errorMessage ? `form_input_${name}_error_message` : undefined}
          onClick={onClickHandler}
          onChange={onChangeHandler}
        >
          Yes
        </FormInputCheckbox>
        <FormInputCheckbox
          ref={buttonNoRef}
          name={`${name}-no`}
          typeRadio
          radioGroupName={name}
          aria-errormessage={!valid && errorMessage ? `form_input_${name}_error_message` : undefined}
          onClick={onClickHandler}
          onChange={onChangeHandler}
        >
          No
        </FormInputCheckbox>
      </div>
      {children}
      {errorMessage && !valid && <p id={`form_input_${name}_error_message`} className="form-input-radio-combo-1__error-message invalid-feedback">{errorMessage}</p>}
    </fieldset>
  );
}

export default forwardRef(FormInputRadioCombo1);